import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Layer, Stage, Rect } from "react-konva";
import RectangleElement from "./rectangle/RectangleElement";
import CircleElement from "./circle/CircleElement";
import UnifiedLineArrowElement from "./unified/UnifiedLineArrowElement";
import ImageElement from "./image/ImageElement";
import TextElement from "./text/TextElement";
import {
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  stripHtmlPreserveFormatting,
} from "../../SectionElements/plugins/utils";
import { normalizeShapeType } from "../../../util/shapeTypeMapper";
import Loader from "../../../common/Loader";
import { refreshCanvasPagesWithLatestParts } from "../../../util/canvas/refreshCanvasPartsWithSearch";

// Strip HTML and normalize whitespace for comparison (avoids duplicate content/task/note)
const normalizeForCompare = (s) => {
  if (!s) return "";
  const plain = stripHtmlPreserveFormatting(String(s));
  return plain.replace(/\s+/g, " ").trim();
};

// Clear content when it duplicates task/note to avoid same text showing twice (black + styled)
const clearContentIfDuplicateOfTaskOrNote = (content, task, note) => {
  if (!content || !normalizeForCompare(content)) return content;
  const c = normalizeForCompare(content);
  const t = normalizeForCompare(task);
  const n = normalizeForCompare(note);
  if (c && t && c === t) return null;
  if (c && n && c === n) return null;
  if (c && t && c.length > 10 && t.includes(c)) return null;
  if (c && n && c.length > 10 && n.includes(c)) return null;
  return content;
};

// ---------------------------------------------------------------------------
// Color helpers
// New canvas format stores stroke/fill colors as decimal integers
// (e.g. 16711680 = #FF0000).  These helpers normalise any value to a valid CSS
// colour string so Konva never receives a raw number.
// ---------------------------------------------------------------------------
const intToHexColor = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "string" && val.trim() !== "") return val;
  if (typeof val === "number" && Number.isFinite(val)) {
    return "#" + Math.max(0, Math.min(0xffffff, Math.round(val))).toString(16).padStart(6, "0");
  }
  return null;
};

const resolveColor = (primary, secondary, fallback = "#000000") =>
  intToHexColor(primary) || intToHexColor(secondary) || fallback;

const isOffCanvasElement = (item = {}) => {
  const shapeProps = item.shape_properties || {};
  const shapeInfo = item.shape_info || {};
  const imageProps = item.image_properties || {};
  const textBoxProps = item.text_box_properties || {};
  const textBoxLoc =
    item.canvas_page_location_and_size ||
    textBoxProps.canvas_page_location_and_size ||
    {};
  return (
    item.isOffcanvas === true ||
    item.isOffCanvas === true ||
    item.is_off_canvas === true ||
    shapeProps.isOffcanvas === true ||
    shapeProps.isOffCanvas === true ||
    shapeProps.is_off_canvas === true ||
    shapeInfo.isOffcanvas === true ||
    shapeInfo.isOffCanvas === true ||
    shapeInfo.is_off_canvas === true ||
    imageProps.isOffcanvas === true ||
    imageProps.isOffCanvas === true ||
    imageProps.is_off_canvas === true ||
    textBoxProps.isOffcanvas === true ||
    textBoxProps.isOffCanvas === true ||
    textBoxProps.is_off_canvas === true ||
    textBoxLoc.isOffcanvas === true ||
    textBoxLoc.isOffCanvas === true ||
    textBoxLoc.is_off_canvas === true
  );
};

// Keep preview image source resolution aligned with MyCanvas image loader:
// prefer edited/cropped `imageDataUrl` first, then fall back to saved paths.
const resolveImageSource = (imageRecord = {}, props = {}) =>
  props.imageDataUrl ||
  imageRecord.imageDataUrl ||
  props.src ||
  imageRecord.src ||
  imageRecord.image_path ||
  props.image_path ||
  "";

const isTopLayerImage = (imageRecord = {}, props = {}) => {
  const fromProps = props.isOnTopLayer ?? props.is_on_top_layer ?? props.onTopLayer;
  const fromRecord =
    imageRecord.isOnTopLayer ??
    imageRecord.is_on_top_layer ??
    imageRecord.onTopLayer;
  const rawLayer = props.layer ?? imageRecord.layer;
  const layer = typeof rawLayer === "string" ? rawLayer.toLowerCase() : "";
  return (
    fromProps === true ||
    fromRecord === true ||
    layer === "top" ||
    layer === "toplayer" ||
    layer === "top-layer"
  );
};

// Canonicalize line/arrow geometry for preview rendering.
// Some payloads contain very negative local points with a positive x/y anchor.
// We shift points to a local 0-based box and move x/y by that shift so the
// absolute on-canvas geometry is preserved and remains visible.
/** Same point extraction as the Konva preview map (Arrow / Line / DSA). */
const extractLinePointsRelative = (props, innerSI, x, y) => {
  const isValidPoints = (arr) => Array.isArray(arr) && arr.length >= 4;
  const isLikelyDefaultPoints = (arr) => {
    if (!isValidPoints(arr) || arr.length !== 4) return false;
    const [x1, y1, x2, y2] = arr.map((v) => Number(v) || 0);
    return (
      (Math.abs(x1) < 0.001 &&
        Math.abs(y1) < 0.001 &&
        Math.abs(x2 - 100) < 0.001 &&
        Math.abs(y2) < 0.001) ||
      (Math.abs(x1) < 0.001 &&
        Math.abs(y1) < 0.001 &&
        Math.abs(x2 - 100) < 0.001 &&
        Math.abs(y2 - 100) < 0.001)
    );
  };

  const propsPoints = isValidPoints(props.points) ? props.points : null;
  const innerPoints = isValidPoints(innerSI.points) ? innerSI.points : null;
  const nestedPropsPoints = isValidPoints(props.shape_properties?.points)
    ? props.shape_properties.points
    : null;
  const nestedInfoPoints = isValidPoints(props.shape_info?.points)
    ? props.shape_info.points
    : null;

  // Some API payloads keep a generic placeholder in top-level `points` while
  // nested `shape_info.points` has the real geometry. Prefer richer nested points.
  let src = null;
  if (innerPoints && (!propsPoints || isLikelyDefaultPoints(propsPoints))) {
    src = innerPoints;
  } else if (nestedInfoPoints && (!propsPoints || isLikelyDefaultPoints(propsPoints))) {
    src = nestedInfoPoints;
  } else if (propsPoints) {
    src = propsPoints;
  } else if (innerPoints) {
    src = innerPoints;
  } else if (nestedPropsPoints) {
    src = nestedPropsPoints;
  } else if (nestedInfoPoints) {
    src = nestedInfoPoints;
  }
  if (!src) return [0, 0, 100, 0];

  const isAbsolute =
    Math.abs((src[0] || 0) - x) < 0.1 && Math.abs((src[1] || 0) - y) < 0.1;

  return isAbsolute
    ? src.map((v, idx) => (idx % 2 === 0 ? v - x : v - y))
    : src;
};

const normalizePreviewLineGeometry = (x, y, points = [], keepAnchor = false) => {
  const sourcePoints =
    Array.isArray(points) && points.length >= 4 ? points : [0, 0, 100, 0];

  const firstX = Number(sourcePoints[0]) || 0;
  const firstY = Number(sourcePoints[1]) || 0;
  const shouldRebaseAroundFirstPoint =
    keepAnchor &&
    Number.isFinite(Number(x)) &&
    Number.isFinite(Number(y)) &&
    (Math.abs(firstX) > 100 || Math.abs(firstY) > 100) &&
    Math.abs((Number(x) || 0) + firstX) < 200 &&
    Math.abs((Number(y) || 0) + firstY) < 200;

  const localPoints = shouldRebaseAroundFirstPoint
    ? sourcePoints.map((value, index) =>
      index % 2 === 0
        ? (Number(value) || 0) - firstX
        : (Number(value) || 0) - firstY
    )
    : sourcePoints.map((value) => Number(value) || 0);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < localPoints.length; i += 2) {
    const px = Number(localPoints[i]) || 0;
    const py = Number(localPoints[i + 1]) || 0;
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px);
    maxY = Math.max(maxY, py);
  }

  const shiftedPoints = localPoints.map((value, index) =>
    index % 2 === 0
      ? (Number(value) || 0) - minX
      : (Number(value) || 0) - minY
  );

  if (keepAnchor) {
    // skipOffset means points are already in local space and should not be
    // re-anchored to the first point. Re-anchoring to first point changes
    // absolute position when first point is not [0,0] (seen in Stroke 2).
    // Canonicalize by min bounds while preserving absolute geometry.
    return {
      x: (Number(x) || 0) + minX,
      y: (Number(y) || 0) + minY,
      points: shiftedPoints,
      width: Math.max(0, maxX - minX),
      height: Math.max(0, maxY - minY),
    };
  }

  return {
    x: (Number(x) || 0) + minX,
    y: (Number(y) || 0) + minY,
    points: shiftedPoints,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  };
};

// ---------------------------------------------------------------------------
// Content-bounds helper
// Scans every element on a page and returns the furthest-right (maxX) and
// furthest-down (maxY) pixel.  Used to size the Konva Stage exactly around the
// actual content instead of the full viewport, so two-column canvases render
// at the correct proportions (no 260 px of blank space on the right).
// ---------------------------------------------------------------------------
const calculatePageContentBounds = (page) => {
  let maxX = 0;
  let maxY = 0;

  // shape_data / shapes ─ both old and new canvas
  (page.shape_data || page.shapes || []).forEach((shape) => {
    if (isOffCanvasElement(shape)) return;
    const sp = shape.shape_properties || shape.shape_info || {};
    const inner = sp.shape_info || {};
    const x = Number(sp.x ?? inner.x) || 0;
    const y = Number(sp.y ?? inner.y) || 0;
    const normalizedType = normalizeShapeType(shape.type);
    const actualShapeType = sp.type ? normalizeShapeType(sp.type) : normalizedType;
    const strokeW = Number(sp.strokeWidth ?? inner.strokeWidth) || 2;
    const strokePad = strokeW / 2;
    // Arrow heads extend past the polyline end in UnifiedLineArrowElement
    const ARROW_HEAD_PAD = 28;

    if (normalizedType === "Image" || actualShapeType === "Image") {
      return;
    }

    if (actualShapeType === "Rect") {
      const w = Number(sp.width ?? inner.width) || 100;
      const h = Number(sp.height ?? inner.height) || 60;
      const scaleX = Number(sp.scaleX ?? inner.scaleX) || 1;
      const scaleY = Number(sp.scaleY ?? inner.scaleY) || 1;
      maxX = Math.max(maxX, x + w * scaleX + strokePad);
      maxY = Math.max(maxY, y + h * scaleY + strokePad);
      return;
    }

    if (actualShapeType === "Circle") {
      const radius = Number(sp.radius ?? inner.radius) || 0;
      const radiusX = Number(sp.radiusX ?? inner.radiusX) || radius;
      const radiusY = Number(sp.radiusY ?? inner.radiusY) || radius;
      maxX = Math.max(maxX, x + radiusX + strokePad);
      maxY = Math.max(maxY, y + radiusY + strokePad);
      return;
    }

    if (actualShapeType === "Text") {
      const w = Number(sp.width ?? inner.width) || 225;
      const h = Number(sp.height ?? inner.height) || 0;
      const textForCalc = {
        fontSize: Number(sp.fontSize ?? inner.fontSize) || 14,
        width: w,
        partsByType: sp.partsByType ?? inner.partsByType ?? {},
        task: sp.task ?? inner.task ?? "",
        note: sp.note ?? inner.note ?? "",
        content: sp.content ?? inner.content ?? "",
        textStyles: sp.textStyles ?? inner.textStyles ?? {},
      };
      const estimatedH = calculateGroupHeight(textForCalc);
      maxX = Math.max(maxX, x + w + strokePad);
      maxY = Math.max(maxY, y + Math.max(h, estimatedH) + strokePad);
      return;
    }

    if (
      actualShapeType === "Arrow" ||
      actualShapeType === "Line" ||
      actualShapeType === "DoubleSidedArrow"
    ) {
      const keepAnchor = sp.skipOffset === true || inner.skipOffset === true;
      const rel = extractLinePointsRelative(sp, inner, x, y);
      const geom = normalizePreviewLineGeometry(x, y, rel, keepAnchor);
      let pMaxX = geom.x;
      let pMaxY = geom.y;
      for (let i = 0; i < geom.points.length; i += 2) {
        pMaxX = Math.max(pMaxX, geom.x + (Number(geom.points[i]) || 0));
        pMaxY = Math.max(pMaxY, geom.y + (Number(geom.points[i + 1]) || 0));
      }
      const endPad =
        actualShapeType === "Line" ? strokePad + 4 : strokePad + ARROW_HEAD_PAD;
      maxX = Math.max(maxX, pMaxX + endPad);
      maxY = Math.max(maxY, pMaxY + endPad);
      return;
    }

    const w = Number(sp.width ?? inner.width) || 0;
    const h = Number(sp.height ?? inner.height) || 0;
    const scaleX = Number(sp.scaleX ?? inner.scaleX) || 1;
    const scaleY = Number(sp.scaleY ?? inner.scaleY) || 1;
    maxX = Math.max(maxX, x + w * scaleX + strokePad);
    maxY = Math.max(maxY, y + h * scaleY + strokePad);
  });

  // image_data (new canvas)
  (page.image_data || []).forEach((img) => {
    if (isOffCanvasElement(img)) return;
    const p = img.image_properties || {};
    const x = Number(p.x) || 0;
    const y = Number(p.y) || 0;
    const w = Number(p.width || p.originalWidth) || 0;
    const h = Number(p.height || p.originalHeight) || 0;
    const scaleX = Number(p.scaleX) || 1;
    const scaleY = Number(p.scaleY) || 1;
    maxX = Math.max(maxX, x + w * scaleX);
    maxY = Math.max(maxY, y + h * scaleY);
  });

  // text_box_data (new canvas format – text boxes stored separately from shapes)
  (page.text_box_data || []).forEach((tb) => {
    if (isOffCanvasElement(tb)) return;
    const tbp = tb.text_box_properties || {};
    const loc = tbp.canvas_page_location_and_size || {};
    const x = Number(loc.x) || 0;
    const y = Number(loc.y) || 0;
    const w = Number(loc.width) || 0;
    const h = Number(loc.height) || 0;
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  });

  // text_boxes (API format – same as text_box_data but different structure)
  (page.text_boxes || []).forEach((tb) => {
    if (isOffCanvasElement(tb)) return;
    const loc = tb.canvas_page_location_and_size || tb.text_box_properties?.canvas_page_location_and_size || {};
    const x = Number(loc.x) || 0;
    const y = Number(loc.y) || 0;
    const w = Number(loc.width) || 0;
    const h = Number(loc.height) || 0;
    // Estimate actual rendered height for task/note content (parts are not converted here,
    // so this is a conservative lower-bound but still beats the raw stored h).
    const tbProps = tb.text_box_properties || {};
    const textForCalc = {
      fontSize: tbProps.fontSize || 14,
      width: w || 225,
      partsByType: {},
      task: tb.task ?? tbProps.task ?? "",
      note: tb.note ?? tbProps.note ?? "",
      content: tbProps.content ?? "",
      textStyles: {},
    };
    const estimatedH = calculateGroupHeight(textForCalc);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + Math.max(h, estimatedH));
  });

  return { maxX, maxY };
};

// ---------------------------------------------------------------------------
// Convert text_box_content / text_box_content_data array to partsByType format
// (required by TextElement for proper rendering of Part, Note, Task sections)
// ---------------------------------------------------------------------------
const convertTextBoxContentToPartsByType = (contentData = []) => {
  if (!Array.isArray(contentData) || contentData.length === 0) return {};
  const partsByType = {};
  contentData.forEach((entry) => {
    if (!entry) return;
    const partInfo = entry.part_info || null;
    const typeName = partInfo?.type_name || entry.type_name || "Part";
    const typeOrder =
      entry.type_info?.order ??
      partInfo?.type_info?.order ??
      partInfo?.orders ??
      0;
    if (!partsByType[typeName]) {
      partsByType[typeName] = { order: typeOrder, parts: [] };
    } else if (entry.type_info?.order !== undefined) {
      partsByType[typeName].order = entry.type_info.order;
    }
    let partStr = entry.part || "";
    if (!partStr && partInfo) {
      const partNumberPrefix = partInfo.part_number
        ? `(${partInfo.part_number}) `
        : "";
      partStr = `${partNumberPrefix}${partInfo.description || ""}`.trim();
    }
    const noteStr = entry.note || entry.inline_note || "";
    const itemCodeNote =
      entry.itemCodeNote ||
      entry.textbox_note ||
      partInfo?.textbox_note ||
      "";
    partsByType[typeName].parts.push({
      part_ref: entry.part_ref,
      part: partStr,
      qty: entry.qty ?? 0,
      unit: entry.units || entry.unit || "",
      note: noteStr,
      itemCodeNote,
      id: entry.text_box_content_id || `part-${entry.part_ref}-${Math.random()}`,
    });
  });
  return partsByType;
};

// Strip Task/Note from partsByType when we have top-level task/note to avoid duplicate rendering
const stripDuplicateTaskNoteFromPartsByType = (partsByType, task, note) => {
  if (!partsByType || typeof partsByType !== "object") return partsByType;
  const stripHtml = (s) => (s ? String(s).replace(/<[^>]*>/g, "").trim() : "");
  const hasTopLevelTask = stripHtml(task).length > 0;
  const hasTopLevelNote = stripHtml(note).length > 0;
  if (!hasTopLevelTask && !hasTopLevelNote) return partsByType;
  const filtered = { ...partsByType };
  if (hasTopLevelTask && filtered.Task) delete filtered.Task;
  if (hasTopLevelNote && filtered.Note) delete filtered.Note;
  return filtered;
};

// Remove parts whose text duplicates task/note (avoids same text in Part section + Task/Note section)
const stripPartsDuplicatingTaskOrNote = (partsByType, task, note) => {
  if (!partsByType || typeof partsByType !== "object") return partsByType;
  const t = normalizeForCompare(task);
  const n = normalizeForCompare(note);
  if (!t && !n) return partsByType;
  const out = {};
  Object.entries(partsByType).forEach(([typeName, typeData]) => {
    if (!typeData?.parts?.length) {
      out[typeName] = typeData;
      return;
    }
    const filteredParts = typeData.parts.filter((p) => {
      const pText = normalizeForCompare(p?.part);
      if (!pText || pText.length < 10) return true;
      if (t && pText === t) return false;
      if (n && pText === n) return false;
      if (t && t.includes(pText)) return false;
      if (n && n.includes(pText)) return false;
      return true;
    });
    out[typeName] = { ...typeData, parts: filteredParts };
  });
  return out;
};

// ---------------------------------------------------------------------------
// Find matching text_box for a Text shape (by text_box_id or position)
// Returns merged content: { partsByType, content, task, note, text_box_content_data }
// ---------------------------------------------------------------------------
const toNum = (v) => (v === null || v === undefined ? 0 : Number(v) || 0);
const matchTextBoxToShape = (shape, props, textBoxes = []) => {
  if (!Array.isArray(textBoxes) || textBoxes.length === 0) return null;
  const shapeId = shape.shape_id || props.id || props.shape_id;
  const sx = toNum(props.x);
  const sy = toNum(props.y);

  for (const tb of textBoxes) {
    const existingId = props.text_box_id || shape.text_box_id;
    if (existingId && tb.text_box_id && tb.text_box_id === existingId) {
      return tb;
    }
    if (shapeId && tb.text_box_id && tb.text_box_id === shapeId) {
      return tb;
    }
    const loc = tb.canvas_page_location_and_size || tb.text_box_properties?.canvas_page_location_and_size || {};
    const tx = toNum(loc.x);
    const ty = toNum(loc.y);
    if (Math.abs(sx - tx) <= 2 && Math.abs(sy - ty) <= 2) {
      return tb;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// LoadedImageElement – async-loads an image then passes it to ImageElement
// ---------------------------------------------------------------------------
const LoadedImageElement = ({ imageInfo }) => {
  const [loadedImage, setLoadedImage] = useState(null);
  const [loadedOriginalImage, setLoadedOriginalImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const originalSrc =
    imageInfo.originalImageDataUrl || imageInfo.originalImageSrc || imageInfo.src;
  const needsOriginalBeforeFirstPaint =
    !!imageInfo.cropData &&
    !!originalSrc &&
    (Number(imageInfo.originalCropRotation || 0) !== 0 ||
      !!imageInfo.flipHorizontal ||
      !!imageInfo.flipVertical);

  useEffect(() => {
    const image = new Image();
    try {
      if (/^https?:/i.test(imageInfo.src)) {
        image.crossOrigin = "anonymous";
      }
    } catch { }

    image.onload = () => {
      setLoadedImage(image);
      setIsLoading(false);
    };

    image.onerror = (error) => {
      console.error("Error loading image:", error);
      setIsLoading(false);
    };

    image.src = imageInfo.src;

    let originalImage;
    if (originalSrc) {
      originalImage = new Image();
      try {
        if (/^https?:/i.test(originalSrc)) {
          originalImage.crossOrigin = "anonymous";
        }
      } catch { }
      originalImage.onload = () => {
        setLoadedOriginalImage(originalImage);
      };
      originalImage.onerror = () => {
        setLoadedOriginalImage(null);
      };
      originalImage.src = originalSrc;
    } else {
      setLoadedOriginalImage(null);
    }
  }, [imageInfo.src, imageInfo.originalImageDataUrl, imageInfo.originalImageSrc]);

  // Avoid first-paint flash of uncropped bitmap for straighten/rotated crops.
  if (
    isLoading ||
    !loadedImage ||
    (needsOriginalBeforeFirstPaint && !loadedOriginalImage)
  ) {
    return null;
  }

  const finalImageInfo = {
    ...imageInfo,
    image: loadedImage,
    originalImage: loadedOriginalImage || loadedImage,
  };

  return (
    <ImageElement
      isDraggable={false}
      isPreview={true}
      containerRotationZero={true}
      img={finalImageInfo}
    />
  );
};

/**
 * CanvasPageRenderer – renders canvas pages from JSON data.
 *
 * Handles both the OLD canvas format (flat shape_properties, canva_snap_url) and
 * the NEW canvas format (nested shape_info inside shape_properties, image_data,
 * text_box_data, no snapshot URL).
 *
 * @param {string}  [props.url]           URL to fetch the JSON data
 * @param {Object}  [props.canvasData]    Direct JSON data (if URL not provided)
 * @param {number}  [props.width]         Stage width (defaults to 25 % of window)
 * @param {boolean} [props.showSnapshots] Show snapshot images when available (default: true)
 * @param {Object}  [props.fetchOptions]  Optional fetch options
 * @param {boolean} [props.constrainToContainer] Constrain to parent container
 * @param {boolean} [props.hideStageScrollbars] Hide stage internal scrollbars
 * @param {boolean} [props.skipLatestPartsRefresh] Keep incoming parts payload as-is
 */
const CanvasPageRenderer = ({
  url,
  canvasData: directCanvasData,
  width = window.innerWidth * 0.25,
  showSnapshots = true,
  fetchOptions = {},
  constrainToContainer = false,
  hideStageScrollbars = false,
  skipLatestPartsRefresh = false,
}) => {
  const dispatch = useDispatch();
  const [canvasData, setCanvasData] = useState(directCanvasData || null);
  const [resolvedPages, setResolvedPages] = useState([]);
  const [loading, setLoading] = useState(!!url);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCanvasData(directCanvasData || null);
  }, [directCanvasData]);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    const fetchCanvasData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...fetchOptions.headers,
          },
          ...fetchOptions,
          mode: "cors",
          credentials: "omit",
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch canvas data: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setCanvasData(data);
      } catch (err) {
        console.error("Error fetching canvas data:", err);
        setError(err.message || "Failed to load canvas data");
      } finally {
        setLoading(false);
      }
    };

    fetchCanvasData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const pages = Array.isArray(canvasData)
    ? canvasData
    : canvasData?.results?.pages || canvasData?.pages || [];

  useEffect(() => {
    let cancelled = false;
    const hydrateLatestParts = async () => {
      if (!Array.isArray(pages) || pages.length === 0) {
        if (!cancelled) setResolvedPages([]);
        return;
      }
      if (skipLatestPartsRefresh) {
        if (!cancelled) setResolvedPages(pages);
        return;
      }
      try {
        const refreshed = await refreshCanvasPagesWithLatestParts(pages, {
          dispatch,
        });
        if (!cancelled) setResolvedPages(refreshed || pages);
      } catch {
        if (!cancelled) setResolvedPages(pages);
      }
    };
    hydrateLatestParts();
    return () => {
      cancelled = true;
    };
  }, [pages, dispatch, skipLatestPartsRefresh]);

  const sortedPages = (resolvedPages.length > 0 ? resolvedPages : pages)
    .filter((p) => p && (p.canvas_page_id || p.page_id || p.shape_data || p.canva_snap_url))
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateA - dateB;
    });

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", color: "red" }}>
        <h3>Error loading canvas data</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!pages || pages.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        No canvas pages to render
      </div>
    );
  }

  return (
    <>
      {sortedPages.map((page, pageIndex) => {
        // ── Stage dimensions ──────────────────────────────────────────────
        const resolvePageHeightVh = (...vals) => {
          for (const v of vals) {
            const n = typeof v === "number" ? v : parseInt(v, 10);
            if (Number.isFinite(n) && n > 0) return n;
          }
          return 100;
        };
        const pageHeightVh = resolvePageHeightVh(
          page?.layout_config?.layout?.page_height_vh,
          page?.canvasHeight,
          page?.elements?.canvasHeight,
          page?.layout?.page_height_vh,
        );
        const canvasHeight = String(pageHeightVh);
        const stageHeight = Math.max(
          parseInt(canvasHeight) <= 1
            ? 10
            : parseInt(canvasHeight) <= 5
              ? 10
              : parseInt(canvasHeight) < 30
                ? 50
                : 20,
          (parseInt(canvasHeight) / 100) * window.innerHeight - 4
        );

        // Effective dimensions: fit the stage to the actual content bounding box.
        //
        // OLD canvas (900 px stage, content at x≈0-700)  → unchanged behaviour.
        // NEW two-column canvas (content ≈ 640 px wide, stage was 900 px) →
        //   image appeared 28 % smaller than designed and the right column started
        //   at 36 % instead of 50 % of the stage, breaking the two-column layout.
        //   Shrinking effectiveWidth to contentWidth + 40 fixes both issues.
        //
        // effectiveHeight: always expands to show ALL content, even on small
        //   screens where stageHeight (= window.innerHeight - 4) would clip it.
        const CANVAS_PAD = constrainToContainer ? 8 : 40;
        const bounds = calculatePageContentBounds(page);
        const showHeightDebugBorder =
          typeof window !== "undefined" &&
          window.localStorage?.getItem("debugCanvasHeight") === "1";
        if (showHeightDebugBorder) {
        }

        // Logical (design) size in the same coordinate system as saved shapes.
        // Always expand to include full content bounds so shorter viewports do not
        // clip the bottom of the page in constrained previews (CanvasForm cards).
        // We still keep a stageHeight floor so empty/small pages retain baseline size.
        const contentHeight =
          bounds.maxY > 50 ? Math.max(bounds.maxY + CANVAS_PAD, 200) : 0;
        const designHeight = Math.max(stageHeight, contentHeight);

        let designWidth;
        if (constrainToContainer) {
          // FIX: Use full available width for max-width elements instead of just content bounds
          // This ensures max-width elements cover the entire container width
          designWidth = width;
        } else {
          designWidth =
            bounds.maxX > 50 && bounds.maxX + CANVAS_PAD < width
              ? bounds.maxX + CANVAS_PAD
              : width;
        }

        const effectiveWidth = designWidth;
        const effectiveHeight = designHeight;

        // When the card is narrower than the canvas design, scale the stage down so
        // nothing is clipped (Konva clips to width/height; capping width alone hid the right side).
        let stageScale = 1;
        let stagePixelWidth = designWidth;
        let stagePixelHeight = designHeight;
        if (constrainToContainer) {
          const dw = Math.max(1, designWidth);
          stageScale = Math.min(1, width / dw);
          stagePixelWidth = Math.max(1, Math.round(designWidth * stageScale));
          stagePixelHeight = Math.max(1, Math.round(designHeight * stageScale));
        }

        return (
          <div
            key={page.canvas_page_id || page.page_id || pageIndex}
            style={{
              marginBottom: constrainToContainer ? "0" : "20px",
              position: "relative",
              ...(constrainToContainer
                ? { width: "100%", maxWidth: "100%", overflow: "hidden" }
                : {
                    width: `${effectiveWidth}px`,
                    minWidth: `${effectiveWidth}px`,
                    maxWidth: "100%",
                    overflow: "visible",
                  }),
              // ...(showHeightDebugBorder
              //   ? { border: "2px solid red", boxSizing: "border-box" }
              //   : {}),
            }}
          >
            {/* Snapshot path (old canvas with canva_snap_url + showSnapshots=true) */}
            {page.canva_snap_url && showSnapshots ? (
              <div
                style={{
                  width: "100%",
                  height: `${pageHeightVh}vh`,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                }}
              >
                <img
                  src={page.canva_snap_url}
                  alt={`Canvas Snapshot ${pageIndex + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : (
              /* Live Konva rendering path – used for both new canvas and
                 old canvas when showSnapshots=false (document preview). */
              <div
                id={`main-canvas-stage-container-${pageIndex}`}
                style={{
                  position: "relative",
                  left: "0",
                  top: "0",
                  zIndex: 2,
                  textAlign: "left",
                  lineHeight: 0,
                  width: constrainToContainer ? `${stagePixelWidth}px` : undefined,
                  maxWidth: constrainToContainer ? "100%" : undefined,
                  overflow:
                    constrainToContainer || hideStageScrollbars
                      ? "hidden"
                      : undefined,
                  overflowY: hideStageScrollbars ? "hidden" : undefined,
                  scrollbarWidth: hideStageScrollbars ? "none" : undefined,
                  msOverflowStyle: hideStageScrollbars ? "none" : undefined,
                  // ...(showHeightDebugBorder
                  //   ? { border: "2px dashed red", boxSizing: "border-box" }
                  //   : {}),
                }}
              >
                <Stage
                  width={stagePixelWidth}
                  height={stagePixelHeight}
                  scaleX={stageScale - 0.005}
                  scaleY={stageScale}
                  pixelRatio={window.devicePixelRatio || 1}
                  style={{
                    touchAction: "none",
                    background: "transparent",
                    border: "none",
                    boxShadow: "none",
                    imageRendering: "auto",
                    position: "relative",
                    zIndex: 3,
                    display: "block",
                    margin: 0,
                    verticalAlign: "top",
                    // ...(showHeightDebugBorder
                    //   ? { border: "2px dotted red", boxSizing: "border-box" }
                    //   : {}),
                  }}
                  padding={parseInt(canvasHeight) < 30 ? 10 : 20}
                >
                  {/* ── Layer 1: Images ─────────────────────────────────── */}
                  <Layer>
                    {parseInt(canvasHeight) >= 5 && (
                      <Rect
                        x={parseInt(canvasHeight) < 30 ? -50 : -100}
                        y={parseInt(canvasHeight) < 30 ? -50 : -100}
                        width={effectiveWidth + (parseInt(canvasHeight) < 30 ? 100 : 200)}
                        height={effectiveHeight + (parseInt(canvasHeight) < 30 ? 100 : 200)}
                        fill="transparent"
                      />
                    )}

                    {/* image_data array (new canvas) */}
                    {page.image_data?.map((image, i) => {
                      if (isOffCanvasElement(image)) return null;
                      const p = image.image_properties || {};
                      if (isTopLayerImage(image, p)) return null;
                      const imageInfo = {
                        id: image.image_id || p.id || `img-${i}`,
                        src: resolveImageSource(image, p),
                        x: p.x || 0,
                        y: p.y || 0,
                        width: p.width || p.originalWidth || 100,
                        height: p.height || p.originalHeight || 100,
                        scaleX: p.scaleX || 1,
                        scaleY: p.scaleY || 1,
                        rotation: p.rotation || 0,
                        flipHorizontal: p.flipHorizontal || false,
                        flipVertical: p.flipVertical || false,
                        cropData: p.cropData,
                        originalCropRotation: p.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth,
                        originalHeight: p.originalHeight,
                        imageDataUrl: p.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={image.image_id || `img-${i}`} imageInfo={imageInfo} />;
                    })}

                    {/* images array (backward compat) */}
                    {!page.image_data && page.images?.map((image, i) => {
                      if (isOffCanvasElement(image)) return null;
                      const p = image.image_properties || {};
                      if (isTopLayerImage(image, p)) return null;
                      const imageInfo = {
                        id: image.image_id || p.id || `img-${i}`,
                        src: resolveImageSource(image, p),
                        x: p.x || 0,
                        y: p.y || 0,
                        width: p.width || p.originalWidth || 100,
                        height: p.height || p.originalHeight || 100,
                        scaleX: p.scaleX || 1,
                        scaleY: p.scaleY || 1,
                        rotation: p.rotation || 0,
                        flipHorizontal: p.flipHorizontal || false,
                        flipVertical: p.flipVertical || false,
                        cropData: p.cropData,
                        originalCropRotation: p.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth,
                        originalHeight: p.originalHeight,
                        imageDataUrl: p.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={image.image_id || `img-${i}`} imageInfo={imageInfo} />;
                    })}

                    {/* Image shapes from shape_data – only when no image_data/images (avoids duplicate) */}
                    {!page.image_data?.length && !page.images?.length && page.shape_data?.map((shape, i) => {
                      if (isOffCanvasElement(shape)) return null;
                      if (normalizeShapeType(shape.type) !== "Image") return null;
                      const p = shape.shape_properties || shape.shape_info || {};
                      const innerP = p.shape_info || {};
                      if (isTopLayerImage(shape, { ...innerP, ...p })) return null;
                      const imageInfo = {
                        id: shape.shape_id || p.id || `shape-img-${i}`,
                        src: resolveImageSource(shape, {
                          ...innerP,
                          ...p,
                        }),
                        x: Number(p.x ?? innerP.x) || 0,
                        y: Number(p.y ?? innerP.y) || 0,
                        width: Number(p.width ?? innerP.width) || Number(p.originalWidth) || 100,
                        height: Number(p.height ?? innerP.height) || Number(p.originalHeight) || 100,
                        scaleX: p.scaleX || innerP.scaleX || 1,
                        scaleY: p.scaleY || innerP.scaleY || 1,
                        rotation: p.rotation ?? innerP.rotation ?? 0,
                        flipHorizontal: p.flipHorizontal || innerP.flipHorizontal || false,
                        flipVertical: p.flipVertical || innerP.flipVertical || false,
                        cropData: p.cropData ?? innerP.cropData,
                        originalCropRotation: p.originalCropRotation ?? innerP.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? innerP.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth || innerP.originalWidth,
                        originalHeight: p.originalHeight || innerP.originalHeight,
                        imageDataUrl: p.imageDataUrl || innerP.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl || innerP.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio ?? innerP.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name || innerP.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={shape.shape_id || `shape-img-${i}`} imageInfo={imageInfo} />;
                    })}

                    {/* Image shapes from shapes[] (no shape_data) */}
                    {!page.image_data?.length && !page.images?.length && !page.shape_data && page.shapes?.map((shape, i) => {
                      if (isOffCanvasElement(shape)) return null;
                      if (normalizeShapeType(shape.type) !== "Image") return null;
                      const p = shape.shape_properties || {};
                      if (isTopLayerImage(shape, p)) return null;
                      const imageInfo = {
                        id: shape.shape_id || p.id || `shape-img-${i}`,
                        src: resolveImageSource(shape, p),
                        x: p.x || 0,
                        y: p.y || 0,
                        width: p.width || p.originalWidth || 100,
                        height: p.height || p.originalHeight || 100,
                        scaleX: p.scaleX || 1,
                        scaleY: p.scaleY || 1,
                        rotation: p.rotation || 0,
                        flipHorizontal: p.flipHorizontal || false,
                        flipVertical: p.flipVertical || false,
                        cropData: p.cropData,
                        originalCropRotation: p.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth,
                        originalHeight: p.originalHeight,
                        imageDataUrl: p.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={shape.shape_id || `shape-img-${i}`} imageInfo={imageInfo} />;
                    })}
                  </Layer>

                  {/* ── Layer 2: Shapes, Arrows, Lines, Text ─────────────── */}
                  <Layer>
                    {(page.shape_data || page.shapes || [])?.map((shape, i) => {
                      if (isOffCanvasElement(shape)) return null;
                      const normalizedType = normalizeShapeType(shape.type);

                      // Images are in Layer 1
                      if (normalizedType === "Image") return null;

                      // ── Resolve props for both old and new canvas ──────────
                      // OLD canvas: shape_properties is flat  → { x, y, fill, stroke, ... }
                      // NEW canvas: shape_properties contains metadata at the top level AND
                      //             the actual rendering coords inside a nested shape_info.
                      //             Top-level values are also duplicated for convenience.
                      // Strategy: use top-level props first, fall back to innerSI.
                      const props = shape.shape_properties || shape.shape_info || {};
                      const innerSI = props.shape_info || {};

                      const shapeId = shape.shape_id || props.id || props.shape_id || innerSI.id || `shape-${i}`;

                      // CRITICAL: new canvas stores arrows as type="Line" at the top level
                      // but type="Arrow" inside shape_properties.  Use shape_properties.type
                      // when available so arrows render as arrows, not lines.
                      const actualShapeType = props.type
                        ? normalizeShapeType(props.type)
                        : normalizedType;

                      // Coordinate helpers with nested fallback
                      const sx = Number(props.x ?? innerSI.x) || 0;
                      const sy = Number(props.y ?? innerSI.y) || 0;
                      const sw = Number(props.width ?? innerSI.width) || 100;
                      const sh = Number(props.height ?? innerSI.height) || 60;

                      // Color helpers – new canvas stores strokeColor as a decimal int
                      const sStroke = resolveColor(props.stroke ?? innerSI.stroke, props.strokeColor ?? innerSI.strokeColor, "#000000");
                      const sFill   = resolveColor(props.fill ?? innerSI.fill, props.fillColor ?? innerSI.fillColor, "#FFFFFF");

                      // ── Rect ─────────────────────────────────────────────
                      if (actualShapeType === "Rect") {
                        return (
                          <RectangleElement
                            key={shapeId}
                            rectangle={{
                              ...props,
                              shape_info: undefined, // strip nested object to avoid prop pollution
                              id: shapeId,
                              x: sx,
                              y: sy,
                              width: sw,
                              height: sh,
                              fill: sFill,
                              fillColor: sFill,
                              stroke: sStroke,
                              strokeColor: sStroke,
                              strokeSize: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                              strokeWidth: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                              opacity: props.opacity ?? innerSI.opacity ?? 1,
                              fillOpacity: props.fillOpacity ?? innerSI.fillOpacity ?? 1,
                              strokeOpacity: props.strokeOpacity ?? innerSI.strokeOpacity ?? 1,
                              rotation: props.rotation ?? innerSI.rotation ?? 0,
                              align: props.align ?? innerSI.align ?? "center",
                              text: props.text ?? innerSI.text ?? "",
                            }}
                            isDraggable={false}
                            strokeColor={sStroke}
                            canvasWidth={effectiveWidth}
                            canvasHeight={effectiveHeight}
                            onClick={() => {}}
                            onDragEnd={() => {}}
                            onTransformEnd={() => {}}
                          />
                        );
                      }

                      // ── Circle ───────────────────────────────────────────
                      if (actualShapeType === "Circle") {
                        return (
                          <CircleElement
                            key={shapeId}
                            circle={{
                              ...props,
                              shape_info: undefined,
                              id: shapeId,
                              x: sx,
                              y: sy,
                              radius: props.radius ?? innerSI.radius ?? 50,
                              fill: sFill,
                              fillColor: sFill,
                              stroke: sStroke,
                              strokeColor: sStroke,
                              strokeSize: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                              strokeWidth: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                              opacity: props.opacity ?? innerSI.opacity ?? 1,
                              fillOpacity: props.fillOpacity ?? innerSI.fillOpacity ?? 1,
                              strokeOpacity: props.strokeOpacity ?? innerSI.strokeOpacity ?? 1,
                            }}
                            isDraggable={false}
                            strokeColor={sStroke}
                            canvasWidth={effectiveWidth}
                            canvasHeight={effectiveHeight}
                            onClick={() => {}}
                            onDragEnd={() => {}}
                            onTransformEnd={() => {}}
                          />
                        );
                      }

                      // ── Arrow ────────────────────────────────────────────
                      if (actualShapeType === "Arrow") {
                        const arrowStroke = resolveColor(props.stroke ?? innerSI.stroke, props.strokeColor ?? innerSI.strokeColor, "#000000");
                        const keepAnchor =
                          props.skipOffset === true || innerSI.skipOffset === true;
                        const normalizedGeometry = normalizePreviewLineGeometry(
                          sx,
                          sy,
                          extractLinePointsRelative(props, innerSI, sx, sy),
                          keepAnchor
                        );
                        const unifiedElement = {
                          ...props,
                          shape_info: undefined,
                          id: shapeId,
                          x: normalizedGeometry.x,
                          y: normalizedGeometry.y,
                          points: normalizedGeometry.points,
                          width: normalizedGeometry.width || sw,
                          height: normalizedGeometry.height || sh,
                          stroke: arrowStroke,
                          strokeColor: arrowStroke,
                          strokeWidth: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                          fill: resolveColor(props.fill ?? innerSI.fill, props.fillColor ?? innerSI.fillColor, arrowStroke),
                          hasStartArrow: props.hasStartArrow !== undefined
                            ? props.hasStartArrow
                            : (innerSI.hasStartArrow !== undefined ? innerSI.hasStartArrow : (props.pointerAtBeginning || false)),
                          hasEndArrow: props.hasEndArrow !== undefined
                            ? props.hasEndArrow
                            : (innerSI.hasEndArrow !== undefined ? innerSI.hasEndArrow : (props.pointerAtEnd !== undefined ? props.pointerAtEnd : true)),
                          type: "Arrow",
                        };
                        return (
                          <UnifiedLineArrowElement
                            key={shapeId}
                            element={unifiedElement}
                            strokeColor={arrowStroke}
                            isDraggable={false}
                            hideBlueCircles={true}
                            canvasWidth={effectiveWidth}
                            canvasHeight={effectiveHeight}
                            onDragEnd={() => {}}
                          />
                        );
                      }

                      // ── Line ─────────────────────────────────────────────
                      if (actualShapeType === "Line") {
                        const lineStroke = resolveColor(props.stroke ?? innerSI.stroke, props.strokeColor ?? innerSI.strokeColor, resolveColor(props.fillColor, null, "#000000"));
                        const keepAnchor =
                          props.skipOffset === true || innerSI.skipOffset === true;
                        const normalizedGeometry = normalizePreviewLineGeometry(
                          sx,
                          sy,
                          extractLinePointsRelative(props, innerSI, sx, sy),
                          keepAnchor
                        );
                        const unifiedElement = {
                          ...props,
                          shape_info: undefined,
                          id: shapeId,
                          x: normalizedGeometry.x,
                          y: normalizedGeometry.y,
                          points: normalizedGeometry.points,
                          width: normalizedGeometry.width || sw,
                          height: normalizedGeometry.height || sh,
                          stroke: lineStroke,
                          strokeColor: lineStroke,
                          strokeWidth: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                          hasStartArrow: props.hasStartArrow !== undefined
                            ? props.hasStartArrow
                            : (innerSI.hasStartArrow !== undefined ? innerSI.hasStartArrow : (props.pointerAtBeginning || false)),
                          hasEndArrow: props.hasEndArrow !== undefined
                            ? props.hasEndArrow
                            : (innerSI.hasEndArrow !== undefined ? innerSI.hasEndArrow : (props.pointerAtEnd || false)),
                          type: "Line",
                        };
                        return (
                          <UnifiedLineArrowElement
                            key={shapeId}
                            element={unifiedElement}
                            strokeColor={lineStroke}
                            isDraggable={false}
                            hideBlueCircles={true}
                            canvasWidth={effectiveWidth}
                            canvasHeight={effectiveHeight}
                            onDragEnd={() => {}}
                          />
                        );
                      }

                      // ── DoubleSidedArrow ─────────────────────────────────
                      if (actualShapeType === "DoubleSidedArrow") {
                        const dsaStroke = resolveColor(props.stroke ?? innerSI.stroke, props.strokeColor ?? innerSI.strokeColor, resolveColor(props.fillColor, null, "#000000"));
                        const keepAnchor =
                          props.skipOffset === true || innerSI.skipOffset === true;
                        const normalizedGeometry = normalizePreviewLineGeometry(
                          sx,
                          sy,
                          extractLinePointsRelative(props, innerSI, sx, sy),
                          keepAnchor
                        );
                        const unifiedElement = {
                          ...props,
                          shape_info: undefined,
                          id: shapeId,
                          x: normalizedGeometry.x,
                          y: normalizedGeometry.y,
                          points: normalizedGeometry.points,
                          width: normalizedGeometry.width || sw,
                          height: normalizedGeometry.height || sh,
                          stroke: dsaStroke,
                          strokeColor: dsaStroke,
                          strokeWidth: props.strokeWidth ?? innerSI.strokeWidth ?? 2,
                          fill: resolveColor(props.fill ?? innerSI.fill, props.fillColor ?? innerSI.fillColor, dsaStroke),
                          hasStartArrow: props.hasStartArrow !== undefined
                            ? props.hasStartArrow
                            : (innerSI.hasStartArrow !== undefined ? innerSI.hasStartArrow : true),
                          hasEndArrow: props.hasEndArrow !== undefined
                            ? props.hasEndArrow
                            : (innerSI.hasEndArrow !== undefined ? innerSI.hasEndArrow : true),
                          type: "DoubleSidedArrow",
                        };
                        return (
                          <UnifiedLineArrowElement
                            key={shapeId}
                            element={unifiedElement}
                            strokeColor={dsaStroke}
                            isDraggable={false}
                            hideBlueCircles={true}
                            canvasWidth={effectiveWidth}
                            canvasHeight={effectiveHeight}
                            onDragEnd={() => {}}
                          />
                        );
                      }

                      // ── Text (old canvas text boxes stored in shape_data) ─
                      if (actualShapeType === "Text") {
                        let rotation = props.rotation ?? innerSI.rotation ?? 0;
                        if (shape.canvas_page_location_and_size?.rotation !== undefined) {
                          rotation = shape.canvas_page_location_and_size.rotation;
                        } else if (props.canvas_page_location_and_size?.rotation !== undefined) {
                          rotation = props.canvas_page_location_and_size.rotation;
                        }

                        // CRITICAL: Merge with text_boxes (API format) so we get full content
                        const textBoxes = page.text_boxes || [];
                        const matchedTextBox = matchTextBoxToShape(shape, props, textBoxes);

                        let extractedPartsByType =
                          props.shape_properties?.partsByType ||
                          shape.shape_properties?.partsByType ||
                          props.partsByType;
                        if (!extractedPartsByType && props.shape_info?.partsByType) {
                          extractedPartsByType = props.shape_info.partsByType;
                        }
                        if (!extractedPartsByType && shape.shape_info?.partsByType) {
                          extractedPartsByType = shape.shape_info.partsByType;
                        }

                        // If we have text_box_content from API but no partsByType, convert it
                        let contentData = props.text_box_content_data || [];
                        if (matchedTextBox && Array.isArray(matchedTextBox.text_box_content) && matchedTextBox.text_box_content.length > 0) {
                          contentData = matchedTextBox.text_box_content.map((e) => ({ ...e, text_box_id: matchedTextBox.text_box_id }));
                        } else if (contentData.length === 0 && Array.isArray(page.text_box_content_data)) {
                          const tbId = props.text_box_id || shapeId;
                          contentData = page.text_box_content_data.filter((e) => e && (e.text_box_id === tbId || e.text_box_id === shapeId));
                        }
                        if (!extractedPartsByType && contentData.length > 0) {
                          extractedPartsByType = convertTextBoxContentToPartsByType(contentData);
                        }

                        const tbProps = matchedTextBox?.text_box_properties || props.text_box_properties || {};
                        const contentFromBox = matchedTextBox ? (tbProps.content ?? props.content) : props.content;
                        const taskFromBox = matchedTextBox ? (tbProps.task ?? props.task) : props.task;
                        const noteFromBox = matchedTextBox ? (tbProps.note ?? props.note) : props.note;

                        let finalPartsByType = stripDuplicateTaskNoteFromPartsByType(
                          extractedPartsByType || {},
                          taskFromBox || props.task,
                          noteFromBox || props.note
                        );
                        finalPartsByType = stripPartsDuplicatingTaskOrNote(
                          finalPartsByType,
                          taskFromBox || props.task,
                          noteFromBox || props.note
                        );

                        const rawContent = contentFromBox || props.content || null;
                        const taskVal = taskFromBox || props.task || "";
                        const noteVal = noteFromBox || props.note || "";
                        const contentVal = clearContentIfDuplicateOfTaskOrNote(rawContent, taskVal, noteVal);

                        const textInfo = {
                          ...props,
                          shape_info: undefined,
                          id: shapeId,
                          x: sx,
                          y: sy,
                          width: sw !== 100 ? sw : (props.width || 225),
                          height: sh !== 60 ? sh : (props.height || 100),
                          showBackground: props.showBackground ?? true,
                          content: contentVal,
                          contentPlainText: props.contentPlainText || "",
                          contentIsHtml: props.contentIsHtml ?? (props.content ? true : false),
                          fontSize: props.fontSize || tbProps.fontSize || 14,
                          fillColor: resolveColor(props.fillColor, tbProps.fillColor, "#ffffff"),
                          strokeColor: resolveColor(props.strokeColor, props.stroke, "#000000"),
                          strokeWidth: props.strokeWidth || props.strokeSize || 2,
                          textStyles: props.textStyles || {},
                          partsByType: finalPartsByType,
                          text_box_id: props.text_box_id || matchedTextBox?.text_box_id,
                          text_box_properties: props.text_box_properties || tbProps || {},
                          text_box_content_data: contentData,
                          rotation,
                          name: props.name || "",
                          qty: props.qty || 0,
                          part: props.part || "",
                          task: taskFromBox || props.task || "",
                          note: noteFromBox || props.note || "",
                          unit: props.unit || "",
                        };

                        return (
                          <TextElement
                            key={shapeId}
                            text={textInfo}
                            hasUserContent={hasUserContent}
                            setSelectedObject={() => {}}
                            setShowTextBox={() => {}}
                            setSelectedImage={() => {}}
                            isDraggable={false}
                            getTextStyle={getTextStyle}
                            stripHtmlPreserveFormatting={stripHtmlPreserveFormatting}
                            estimateKonvaTextHeight={estimateKonvaTextHeight}
                            formatListContent={formatListContent}
                            hasNoteContent={hasNoteContent}
                            hasTaskContent={hasTaskContent}
                            textGroupHeight={calculateGroupHeight(textInfo)}
                            textBlockWidth={calculateTextBlockWidth(textInfo)}
                            canvasWidth={effectiveWidth}
                            canvasHeight={effectiveHeight}
                          />
                        );
                      }

                      return null;
                    })}

                    {/* ── text_boxes (API format) – render when no shape_data Text for this box ─
                        API returns text_boxes with text_box_content. Only render boxes that
                        were NOT already rendered from shape_data (avoid duplicates).        */}
                    {page.text_boxes?.map((textBox, i) => {
                      const tbId = textBox.text_box_id || `api-tb-${i}`;
                      const tbProps = textBox.text_box_properties || {};
                      const loc = textBox.canvas_page_location_and_size || tbProps.canvas_page_location_and_size || {};
                      const style = tbProps.text_box_properties || tbProps;
                      const contentData = (textBox.text_box_content || []).map((e) => ({ ...e, text_box_id: tbId }));

                      if (loc.x === undefined && loc.y === undefined && !loc.width) return null;

                      const wasRenderedFromShape = (page.shape_data || page.shapes || []).some((s) => {
                        if (normalizeShapeType(s.type) !== "Text") return false;
                        const sp = s.shape_properties || s.shape_info || {};
                        if (s.shape_id === tbId || sp.text_box_id === tbId) return true;
                        const sx = toNum(sp.x ?? sp.shape_info?.x);
                        const sy = toNum(sp.y ?? sp.shape_info?.y);
                        const tx = toNum(loc.x);
                        const ty = toNum(loc.y);
                        return Math.abs(sx - tx) <= 2 && Math.abs(sy - ty) <= 2;
                      });
                      if (wasRenderedFromShape) return null;

                      let partsByType = stripDuplicateTaskNoteFromPartsByType(
                        convertTextBoxContentToPartsByType(contentData),
                        tbProps.task,
                        tbProps.note
                      );
                      partsByType = stripPartsDuplicatingTaskOrNote(partsByType, tbProps.task, tbProps.note);
                      const rawContent = Object.keys(partsByType).length > 0 ? null : (tbProps.content || "");
                      const contentVal = rawContent ? clearContentIfDuplicateOfTaskOrNote(rawContent, tbProps.task, tbProps.note) : rawContent;
                      const textInfo = {
                        id: tbId,
                        x: Number(loc.x) || 0,
                        y: Number(loc.y) || 0,
                        width: Number(loc.width) || 225,
                        height: Number(loc.height) || 100,
                        rotation: Number(loc.rotation) || 0,
                        showBackground: true,
                        content: contentVal,
                        contentPlainText: Object.keys(partsByType).length > 0 || !contentVal ? "" : (tbProps.contentPlainText || ""),
                        contentIsHtml: Object.keys(partsByType).length > 0 || !contentVal ? false : (tbProps.contentIsHtml ?? false),
                        fontSize: style.fontSize || 14,
                        fillColor: resolveColor(style.backgroundColor, style.fillColor, "#ffffff"),
                        strokeColor: resolveColor(style.strokeColor, null, "#000000"),
                        strokeWidth: style.strokeWidth || 2,
                        textStyles: { fontSize: style.fontSize || 14, color: style.color || "#000000" },
                        partsByType,
                        text_box_id: tbId,
                        text_box_properties: style,
                        text_box_content_data: contentData,
                        note: tbProps.note || "",
                        task: tbProps.task || "",
                        name: "",
                        qty: 0,
                        part: "",
                        unit: "",
                      };

                      return (
                        <TextElement
                          key={`api-tb-${tbId}-${i}`}
                          text={textInfo}
                          hasUserContent={hasUserContent}
                          setSelectedObject={() => {}}
                          setShowTextBox={() => {}}
                          setSelectedImage={() => {}}
                          isDraggable={false}
                          getTextStyle={getTextStyle}
                          stripHtmlPreserveFormatting={stripHtmlPreserveFormatting}
                          estimateKonvaTextHeight={estimateKonvaTextHeight}
                          formatListContent={formatListContent}
                          hasNoteContent={hasNoteContent}
                          hasTaskContent={hasTaskContent}
                          textGroupHeight={calculateGroupHeight(textInfo)}
                          textBlockWidth={calculateTextBlockWidth(textInfo)}
                          canvasWidth={effectiveWidth}
                          canvasHeight={effectiveHeight}
                        />
                      );
                    })}

                    {/* ── text_box_data (NEW canvas format) ────────────────
                        In the new canvas, text boxes are stored in a separate
                        text_box_data array rather than as shape_data[type="Text"].
                        Each entry has:
                          text_box_properties.canvas_page_location_and_size → x,y,w,h,rotation
                          text_box_properties.text_box_properties → fontSize, color
                          text_box_properties.note / .task
                          text_box_content_data → part references
                        Skip if already rendered from shape_data or text_boxes.  */}
                    {page.text_box_data?.map((textBox, i) => {
                      const tbProps = textBox.text_box_properties || {};
                      const loc     = tbProps.canvas_page_location_and_size || textBox.canvas_page_location_and_size || {};
                      const style   = tbProps.text_box_properties || tbProps;
                      let contentData = textBox.text_box_content_data || [];

                      // CRITICAL: When content is at page level (text_box_content_data), merge it
                      if (contentData.length === 0 && Array.isArray(page.text_box_content_data) && page.text_box_content_data.length > 0) {
                        const tbId = tbProps.text_box_id;
                        if (tbId) {
                          contentData = page.text_box_content_data.filter((e) => e && e.text_box_id === tbId);
                        } else if (page.text_box_data.length === 1) {
                          contentData = page.text_box_content_data;
                        } else {
                          const uniqueIds = [...new Set(page.text_box_content_data.map((e) => e?.text_box_id).filter(Boolean))];
                          if (i < uniqueIds.length) {
                            const matchId = uniqueIds[i];
                            contentData = page.text_box_content_data.filter((e) => e && e.text_box_id === matchId);
                          }
                        }
                      }

                      // Skip if there is no usable position data
                      if (loc.x === undefined && loc.y === undefined && !loc.width) return null;

                      const textBoxId = tbProps.text_box_id || (contentData[0]?.text_box_id) || `tb-${i}`;

                      // Skip if already rendered from shape_data (avoid duplicate - match by id or position)
                      const tbX = Number(loc.x) || 0;
                      const tbY = Number(loc.y) || 0;
                      const wasRenderedFromShape = (page.shape_data || page.shapes || []).some((s) => {
                        if (normalizeShapeType(s.type) !== "Text") return false;
                        const sp = s.shape_properties || s.shape_info || {};
                        if (s.shape_id === textBoxId || sp.text_box_id === textBoxId) return true;
                        const sx = toNum(sp.x ?? sp.shape_info?.x);
                        const sy = toNum(sp.y ?? sp.shape_info?.y);
                        return Math.abs(sx - tbX) <= 3 && Math.abs(sy - tbY) <= 3;
                      });
                      if (wasRenderedFromShape) return null;

                      let partsByType = stripDuplicateTaskNoteFromPartsByType(
                        convertTextBoxContentToPartsByType(contentData),
                        tbProps.task,
                        tbProps.note
                      );
                      partsByType = stripPartsDuplicatingTaskOrNote(partsByType, tbProps.task, tbProps.note);
                      const rawContent = Object.keys(partsByType).length > 0 ? null : (tbProps.content || "");
                      const contentVal = rawContent ? clearContentIfDuplicateOfTaskOrNote(rawContent, tbProps.task, tbProps.note) : rawContent;

                      const textInfo = {
                        id: textBoxId,
                        x: Number(loc.x) || 0,
                        y: Number(loc.y) || 0,
                        width: Number(loc.width) || 225,
                        height: Number(loc.height) || 100,
                        rotation: Number(loc.rotation) || 0,
                        showBackground: true,
                        content: contentVal,
                        contentPlainText: Object.keys(partsByType).length > 0 || !contentVal ? "" : (tbProps.contentPlainText || ""),
                        contentIsHtml: Object.keys(partsByType).length > 0 || !contentVal ? false : (tbProps.contentIsHtml ?? false),
                        fontSize: style.fontSize || 14,
                        fillColor: style.backgroundColor || style.fillColor || "#ffffff",
                        strokeColor: style.strokeColor || "#000000",
                        strokeWidth: style.strokeWidth || 2,
                        textStyles: {
                          fontSize: style.fontSize || 14,
                          color: style.color || "#000000",
                        },
                        partsByType,
                        text_box_id: textBoxId,
                        text_box_properties: style,
                        text_box_content_data: contentData,
                        note: tbProps.note || "",
                        task: tbProps.task || "",
                        name: "",
                        qty: 0,
                        part: "",
                        unit: "",
                      };

                      return (
                        <TextElement
                          key={`tb-${textBoxId}-${i}`}
                          text={textInfo}
                          hasUserContent={hasUserContent}
                          setSelectedObject={() => {}}
                          setShowTextBox={() => {}}
                          setSelectedImage={() => {}}
                          isDraggable={false}
                          getTextStyle={getTextStyle}
                          stripHtmlPreserveFormatting={stripHtmlPreserveFormatting}
                          estimateKonvaTextHeight={estimateKonvaTextHeight}
                          formatListContent={formatListContent}
                          hasNoteContent={hasNoteContent}
                          hasTaskContent={hasTaskContent}
                          textGroupHeight={calculateGroupHeight(textInfo)}
                          textBlockWidth={calculateTextBlockWidth(textInfo)}
                          canvasWidth={effectiveWidth}
                          canvasHeight={effectiveHeight}
                        />
                      );
                    })}
                  </Layer>

                </Stage>
                <Stage
                  width={stagePixelWidth}
                  height={stagePixelHeight}
                  scaleX={stageScale}
                  scaleY={stageScale}
                  pixelRatio={window.devicePixelRatio || 1}
                  listening={false}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 50,
                    pointerEvents: "none",
                    background: "transparent",
                    border: "none",
                    boxShadow: "none",
                  }}
                >
                  {/* Top-layer images must be rendered in a DOM-above stage so they can visually
                      obscure Html overlays emitted by TextElement (react-konva-utils Html). */}
                  <Layer listening={false}>
                    {page.image_data?.map((image, i) => {
                      if (isOffCanvasElement(image)) return null;
                      const p = image.image_properties || {};
                      if (!isTopLayerImage(image, p)) return null;
                      const imageInfo = {
                        id: image.image_id || p.id || `top-img-${i}`,
                        src: resolveImageSource(image, p),
                        x: p.x || 0,
                        y: p.y || 0,
                        width: p.width || p.originalWidth || 100,
                        height: p.height || p.originalHeight || 100,
                        scaleX: p.scaleX || 1,
                        scaleY: p.scaleY || 1,
                        rotation: p.rotation || 0,
                        flipHorizontal: p.flipHorizontal || false,
                        flipVertical: p.flipVertical || false,
                        cropData: p.cropData,
                        originalCropRotation: p.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth,
                        originalHeight: p.originalHeight,
                        imageDataUrl: p.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={`top-${image.image_id || `img-${i}`}`} imageInfo={imageInfo} />;
                    })}

                    {!page.image_data && page.images?.map((image, i) => {
                      if (isOffCanvasElement(image)) return null;
                      const p = image.image_properties || {};
                      if (!isTopLayerImage(image, p)) return null;
                      const imageInfo = {
                        id: image.image_id || p.id || `top-img-${i}`,
                        src: resolveImageSource(image, p),
                        x: p.x || 0,
                        y: p.y || 0,
                        width: p.width || p.originalWidth || 100,
                        height: p.height || p.originalHeight || 100,
                        scaleX: p.scaleX || 1,
                        scaleY: p.scaleY || 1,
                        rotation: p.rotation || 0,
                        flipHorizontal: p.flipHorizontal || false,
                        flipVertical: p.flipVertical || false,
                        cropData: p.cropData,
                        originalCropRotation: p.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth,
                        originalHeight: p.originalHeight,
                        imageDataUrl: p.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={`top-${image.image_id || `img-${i}`}`} imageInfo={imageInfo} />;
                    })}

                    {!page.image_data?.length && !page.images?.length && page.shape_data?.map((shape, i) => {
                      if (isOffCanvasElement(shape)) return null;
                      if (normalizeShapeType(shape.type) !== "Image") return null;
                      const p = shape.shape_properties || shape.shape_info || {};
                      const innerP = p.shape_info || {};
                      if (!isTopLayerImage(shape, { ...innerP, ...p })) return null;
                      const imageInfo = {
                        id: shape.shape_id || p.id || `top-shape-img-${i}`,
                        src: resolveImageSource(shape, {
                          ...innerP,
                          ...p,
                        }),
                        x: Number(p.x ?? innerP.x) || 0,
                        y: Number(p.y ?? innerP.y) || 0,
                        width: Number(p.width ?? innerP.width) || Number(p.originalWidth) || 100,
                        height: Number(p.height ?? innerP.height) || Number(p.originalHeight) || 100,
                        scaleX: p.scaleX || innerP.scaleX || 1,
                        scaleY: p.scaleY || innerP.scaleY || 1,
                        rotation: p.rotation ?? innerP.rotation ?? 0,
                        flipHorizontal: p.flipHorizontal || innerP.flipHorizontal || false,
                        flipVertical: p.flipVertical || innerP.flipVertical || false,
                        cropData: p.cropData ?? innerP.cropData,
                        originalCropRotation: p.originalCropRotation ?? innerP.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? innerP.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth || innerP.originalWidth,
                        originalHeight: p.originalHeight || innerP.originalHeight,
                        imageDataUrl: p.imageDataUrl || innerP.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl || innerP.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio ?? innerP.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name || innerP.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={`top-${shape.shape_id || `shape-img-${i}`}`} imageInfo={imageInfo} />;
                    })}

                    {!page.image_data?.length && !page.images?.length && !page.shape_data && page.shapes?.map((shape, i) => {
                      if (isOffCanvasElement(shape)) return null;
                      if (normalizeShapeType(shape.type) !== "Image") return null;
                      const p = shape.shape_properties || {};
                      if (!isTopLayerImage(shape, p)) return null;
                      const imageInfo = {
                        id: shape.shape_id || p.id || `top-shape-img-${i}`,
                        src: resolveImageSource(shape, p),
                        x: p.x || 0,
                        y: p.y || 0,
                        width: p.width || p.originalWidth || 100,
                        height: p.height || p.originalHeight || 100,
                        scaleX: p.scaleX || 1,
                        scaleY: p.scaleY || 1,
                        rotation: p.rotation || 0,
                        flipHorizontal: p.flipHorizontal || false,
                        flipVertical: p.flipVertical || false,
                        cropData: p.cropData,
                        originalCropRotation: p.originalCropRotation ?? 0,
                        hasBakedTransforms: p.hasBakedTransforms ?? false,
                        originalWidth: p.originalWidth,
                        originalHeight: p.originalHeight,
                        imageDataUrl: p.imageDataUrl,
                        originalImageDataUrl: p.originalImageDataUrl,
                        maintainAspectRatio: p.maintainAspectRatio,
                        compress: p.compress,
                        name: p.name,
                        referenceUrl: p.referenceUrl,
                        opacity: p.opacity,
                      };
                      if (!imageInfo.src) return null;
                      return <LoadedImageElement key={`top-${shape.shape_id || `shape-img-${i}`}`} imageInfo={imageInfo} />;
                    })}
                  </Layer>
                </Stage>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default CanvasPageRenderer;
